import { Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ConfirmFormModal,
  DrawStage,
  WarningModal,
  WarningModalProps
} from "@toorak/tc-common-fe-sdk";
import { Header } from "../../create-loan/home/Header";
import { setTabView } from "../../stores/SettlementAndPurchase/SettlementAndPurchase.reducer";
import { isOrigUser } from "../../utils/AccessManagement";
import { DrawDetails } from "./DrawDetails";
import { useDrawsStyles } from "./Draws.styles";
import { RootState } from "../../stores/rootReducer";
import { DrawRequestStore } from "../../stores/drawRequest/drawRequest.interface";
import {
  getFieldHistory,
  getDrawDetailsByDrawId
} from "../../stores/drawRequest/drawRequest.action";
import { isBulkDrawViewEnabled } from "./DrawDetailsConstants";
import { updateDrawExcelData } from "../../stores/drawRequest/drawRequest.reducer";
import { resolveDrawsListPath, isDrawAccessDeniedError } from "./drawAccessUtils";

function readExtractedDrawsFromSession(): any[] {
  const excelUploadDataInSession = sessionStorage.getItem("drawExcelUpload");
  if (!excelUploadDataInSession) {
    return [];
  }
  return JSON.parse(excelUploadDataInSession);
}

function resolveExtractedDraws(excelData: any[] | undefined): any[] {
  if (excelData?.length) {
    return excelData;
  }
  return readExtractedDrawsFromSession();
}

export const BulkDrawDetails = () => {
  const navigate = useNavigate();
  const classes = useDrawsStyles();
  const dispatch = useDispatch();
  const isOrig = isOrigUser();
  const { drawId } = useParams<any>() as any;
  const [isBulkDraw] = useState(() => isBulkDrawViewEnabled());
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);
  const [openWarning, setOpenWarning] = useState<WarningModalProps | null>(
    null
  );
  const { excelData, drawDetails } = useSelector<RootState, DrawRequestStore>(
    (state) => state.drawRequestStore,
    shallowEqual
  );
  const excelLen = excelData?.length ?? 0;
  const [extractedDraws, setExtractedDraws] = useState(() =>
    resolveExtractedDraws(excelData)
  );
  const [prevExcelLen, setPrevExcelLen] = useState(excelLen);
  if (excelLen !== prevExcelLen) {
    setPrevExcelLen(excelLen);
    setExtractedDraws(resolveExtractedDraws(excelData));
  }
  const { stage } = drawDetails;
  const { draft, drawSubmitted, DrawInQueue } = DrawStage;
  const lastStageSyncRef = useRef<string>("");

  const goToDrawsList = () => {
    setAccessDeniedOpen(false);
    navigate(resolveDrawsListPath(isOrig));
  };

  useEffect(() => {
    if (!drawId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await dispatch(
          getDrawDetailsByDrawId(drawId, isOrig, undefined, true)
        );
        if (!cancelled) {
          setAccessDeniedOpen(false);
        }
      } catch (err: any) {
        if (!cancelled && isDrawAccessDeniedError(err)) {
          setAccessDeniedOpen(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drawId, dispatch, isOrig]);

  useEffect(() => {
    if (!isBulkDrawViewEnabled()) return;
    const excelUploadDataInSession =
      sessionStorage.getItem("drawExcelUpload");
    if (!excelUploadDataInSession) return;
    const stageKey = `${drawId}|${stage}`;
    if (lastStageSyncRef.current === stageKey) return;
    lastStageSyncRef.current = stageKey;
    const excelUploadData = JSON.parse(excelUploadDataInSession);
    const drawIndex = excelUploadData.findIndex(
      (item: { drawId: string }) => item.drawId.toString() === drawId
    );
    if (drawIndex < 0) return;
    excelUploadData[drawIndex] = { ...excelUploadData[drawIndex], stage };
    sessionStorage.setItem(
      "drawExcelUpload",
      JSON.stringify(excelUploadData)
    );
    dispatch(updateDrawExcelData(excelUploadData));
  }, [stage, drawId, dispatch]);

  useEffect(() => {
    if (isBulkDrawViewEnabled()) {
      dispatch(getFieldHistory(drawId, isOrig));
    }
  }, [drawId, isOrig, dispatch]);

  const handleSelectedDrawChange = (index: number) => {
    const tempObj = structuredClone(extractedDraws);
    const drawIndex = tempObj.findIndex((x: any) => x.selected === true);

    if (drawIndex !== index) {
      if (stage !== drawSubmitted) {
        setOpenWarning({
          isOpen: true,
          header: "Warning",
          handleOptional: () => {
            setOpenWarning(null);
          },
          body: "Are you sure you want to leave? Click on Save button to retain the progress.",
          primaryBtnName: "Okay",
          handleClose: () => {
            tempObj.forEach((element: any) => {
              element.selected = false;
            });
            tempObj[index].selected = true;
            setExtractedDraws(tempObj);
            sessionStorage.setItem("drawExcelUpload", JSON.stringify(tempObj));
            setOpenWarning(null);
            navigate(`/drawDetails/${tempObj[index].drawId}`);
          }
        });
      } else {
        tempObj.forEach((element: any) => {
          element.selected = false;
        });
        tempObj[index].selected = true;
        setExtractedDraws(tempObj);
        sessionStorage.setItem("drawExcelUpload", JSON.stringify(tempObj));
        navigate(`/drawDetails/${tempObj[index].drawId}`);
      }
    }
  };
  const getDrawCount = (type: string) =>
    extractedDraws.filter((key: any) => key.stage === type).length;
  return (
    <Grid item container xs={12} sm={12} md={12} xl={12}>
      <ConfirmFormModal
        isOpen={accessDeniedOpen}
        handleClose={goToDrawsList}
        header="Draw Not Accessible"
        body={
          <div style={{ margin: "1.5rem 2rem", maxWidth: 360 }}>
            This draw is not accessible. Please check the Draw ID.
          </div>
        }
        confirmCondition={false}
        primaryBtnName="Go to Draw List"
        hideSecondaryBtn
        successHandler={goToDrawsList}
        disableBackdropClick
        closeDisable
        size="sm"
      />
      <Grid item container xs={12} sm={12} md={12} xl={12}>
        <Header
          value={isOrig ? 8 : 3}
          setTabValue={setTabView}
          showTab
          showNotificationIcon={false}
        />
        <Grid item container xs={12} sm={12} md={12} xl={12}>
          {isBulkDraw ? (
            <Grid item container xs={12} sm={12} md={12} xl={12}>
              <Grid
                item
                container
                xs={12}
                sm={3}
                md={2}
                xl={2}
                className={classes.excelDraw}
              >
                <Grid
                  item
                  container
                  xs={12}
                  sm={12}
                  md={12}
                  xl={12}
                  className={classes.excelContainer}
                >
                  <div className={classes.countContainer}>
                    <p className={classes.drawTitle}>
                      <span className={classes.drawCount}>
                        {extractedDraws.length}
                      </span>{" "}
                      Draw Requests
                    </p>
                  </div>
                  <div className={classes.individualCount}>
                    <div className={classes.draftContainer}>
                      DRAFT ({getDrawCount(draft)})
                    </div>
                    <div className={classes.submittedContainer}>
                      DRAW SUBMITTED ({getDrawCount(drawSubmitted)})
                    </div>
                    <div className={classes.submittedContainer}>
                      DRAW IN QUEUE ({getDrawCount(DrawInQueue)})
                    </div>
                  </div>
                  {extractedDraws?.length > 0 &&
                    extractedDraws.map((item: any, index: number) => (
                      <button
                        type="button"
                        key={String(item.drawId)}
                        className={
                          !item.selected
                            ? classes.drawInfo
                            : [classes.drawInfo, classes.drawInfoSelected].join(
                                " "
                              )
                        }
                        style={{ textAlign: "left", font: "inherit" }}
                        onClick={() => handleSelectedDrawChange(index)}
                      >
                        <p>{item.drawId}</p>
                        <p>Primary Loan Id</p>
                        {item.selected && (
                          <div
                            className={classes.selected}
                            data-testid="selected-loan"
                          />
                        )}
                        <p>{item.primaryLoanId}</p>
                      </button>
                    ))}
                </Grid>
              </Grid>
              <Grid
                item
                container
                xs={12}
                sm={9}
                md={10}
                xl={10}
                className={classes.drawView}
              >
                <DrawDetails drawId={drawId} isBulkDraw={isBulkDraw} />
              </Grid>
            </Grid>
          ) : (
            <DrawDetails drawId={drawId} isBulkDraw={isBulkDraw} />
          )}
        </Grid>
      </Grid>
      {openWarning && (
        <WarningModal
          isOpen={openWarning.isOpen}
          handleClose={openWarning.handleClose}
          header={openWarning.header}
          handleOptional={openWarning.handleOptional}
          body={openWarning.body}
          primaryBtnName={openWarning.primaryBtnName}
        />
      )}
    </Grid>
  );
};
